import { createFileRoute, notFound } from "@tanstack/react-router";
import { useEffect } from "react";
import { Clock, MapPin, MessageCircle } from "lucide-react";
import { Breadcrumbs, breadcrumbSchema } from "@/components/Breadcrumbs";
import { Gallery } from "@/components/Gallery";
import { Reviews } from "@/components/Reviews";
import { ActionLink } from "@/components/ActionLink";
import { BRAND, getUnit, phoneDisplay } from "@/data/units";
import { whatsappUrl } from "@/lib/links";
import { openTrialDialog } from "@/components/TrialDialog";
import { track } from "@/lib/tracking";
import { PlansTable } from "@/components/PlansTable";
import { DAY_USE, formatBRL } from "@/data/plans";

const SEO: Record<string, { title: string; description: string; h1: string }> = {
  "recreio-le-gusta": {
    title: "Academia com acompanhamento no Recreio | Nook Le Gusta",
    description:
      "Treino personalizado no Recreio dos Bandeirantes: até três alunos por instrutor, correção técnica e acompanhamento em toda a sessão na Nook Le Gusta.",
    h1: "Academia com treino personalizado no Recreio — Le Gusta.",
  },
  "barra-da-tijuca": {
    title: "Academia com treino personalizado na Barra da Tijuca | Nook",
    description:
      "Musculação com acompanhamento na Barra da Tijuca: até três alunos por instrutor e orientação constante do começo ao fim do treino.",
    h1: "Academia com treino personalizado na Barra da Tijuca.",
  },
  "recreio-americas": {
    title: "Academia com acompanhamento na Av. das Américas | Nook",
    description:
      "Studio de musculação com treino personalizado na Av. das Américas, Recreio: até três alunos por instrutor e acompanhamento em cada série.",
    h1: "Academia com treino personalizado na Avenida das Américas.",
  },
};

export const Route = createFileRoute("/unidades/$slug")({
  loader: ({ params }) => {
    const unit = getUnit(params.slug);
    if (!unit) throw notFound();
    return { slug: unit.slug };
  },
  head: ({ params }) => {
    const seo = SEO[params.slug];
    const url = `/unidades/${params.slug}`;
    const absUrl = `${BRAND.site}${url}`;
    if (!seo) {
      return { meta: [{ title: "Unidade não encontrada | Nook" }, { name: "robots", content: "noindex" }] };
    }
    const unit = getUnit(params.slug)!;
    return {
      meta: [
        { title: seo.title },
        { name: "description", content: seo.description },
        { property: "og:title", content: seo.title },
        { property: "og:description", content: seo.description },
        { property: "og:url", content: absUrl },
        { property: "og:type", content: "business.business" },
        { name: "robots", content: "index, follow" },
      ],
      links: [{ rel: "canonical", href: absUrl }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HealthClub",
            "@id": `${BRAND.site}${url}#business`,
            name: unit.name,
            url: `${BRAND.site}${url}`,
            address: {
              "@type": "PostalAddress",
              streetAddress: unit.street,
              addressLocality: unit.city,
              addressRegion: unit.state,
              addressCountry: "BR",
            },
            ...(unit.phone ? { telephone: unit.phone } : {}),
            ...(unit.coordinates
              ? { geo: { "@type": "GeoCoordinates", ...unit.coordinates } }
              : {}),
            sameAs: [unit.instagram],
            parentOrganization: { "@id": `${BRAND.site}/#organization` },
            hasMap: unit.mapsUrl,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbSchema([
              { name: "Início", url: `${BRAND.site}/` },
              { name: "Unidades", url: `${BRAND.site}/unidades` },
              { name: unit.shortName, url: `${BRAND.site}${url}` },
            ]),
          ),
        },
      ],
    };
  },
  component: UnitPage,
});

function UnitPage() {
  const { slug } = Route.useLoaderData();
  const unit = getUnit(slug)!;
  const seo = SEO[slug];

  useEffect(() => {
    track("view_unit", { unit: slug });
  }, [slug]);

  return (
    <>
      <section className="relative overflow-hidden border-b border-border pb-16 pt-28">
        <img
          src={unit.images[0].src}
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 h-full w-full object-cover ${unit.photosPending ? "blur-md scale-105" : ""}`}
        />
        <div className="absolute inset-0 bg-black/70" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Breadcrumbs
            items={[
              { label: "Início", to: "/" },
              { label: "Unidades", to: "/unidades" },
              { label: unit.shortName },
            ]}
          />
          <span className="eyebrow mt-10 block">{unit.neighborhood}</span>
          <h1 className="display mt-5 max-w-4xl text-[2.5rem] sm:text-[3.5rem]">{seo.h1}</h1>
          <p className="mt-7 max-w-xl text-sm leading-relaxed text-white/75">
            Até três alunos por instrutor, acompanhamento constante e uma experiência de treino
            <br /> organizada do início ao fim.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <ActionLink
              onClick={() => {
                track("click_experimental", { where: "unit_hero", unit: slug });
                openTrialDialog("unit_hero");
              }}
            >
              Garanta seu Day Use →
            </ActionLink>
            <ActionLink
              href={whatsappUrl(unit)}
              variant="outline"
              onClick={() => track("click_whatsapp", { where: "unit_hero", unit: slug })}
            >
              Falar com a unidade
            </ActionLink>
          </div>
        </div>
      </section>

      <section className="border-b border-border py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Gallery images={unit.images} />
        </div>
      </section>

      <section className="border-b border-border py-16">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <h2 className="display text-3xl sm:text-4xl">Informações</h2>
            <dl className="mt-8 space-y-6 text-sm">
              <div className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-nook-green" aria-hidden="true" />
                <div>
                  <dt className="eyebrow">Endereço</dt>
                  <dd className="mt-1 text-foreground">{unit.addressLine}</dd>
                </div>
              </div>
              <div className="flex gap-3">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-nook-green" aria-hidden="true" />
                <div>
                  <dt className="eyebrow">Horários</dt>
                  <dd className="mt-1 text-muted-foreground">
                    {unit.hours ? (
                      unit.hours.map((h) => (
                        <div key={h.label}>{h.label}: {h.value}</div>
                      ))
                    ) : (
                      "Grade de horários confirmada pela equipe da unidade no WhatsApp."
                    )}
                  </dd>
                </div>
              </div>
              <div className="flex gap-3">
                <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-nook-green" aria-hidden="true" />
                <div>
                  <dt className="eyebrow">WhatsApp</dt>
                  <dd className="mt-1">
                    <a
                      href={whatsappUrl(unit)}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => track("click_whatsapp", { where: "unit_info", unit: slug })}
                      className="text-foreground hover:text-nook-green"
                    >
                      {phoneDisplay(unit) ?? `Falar com a ${unit.shortName}`}
                    </a>
                  </dd>
                </div>
              </div>
            </dl>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
              <a
                href={unit.mapsUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => track("click_maps", { unit: slug })}
                className="inline-block text-xs uppercase tracking-[0.16em] text-nook-green"
              >
                Veja no Google Maps
              </a>
              <a
                href={unit.wazeUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => track("click_waze", { unit: slug })}
                className="inline-block text-xs uppercase tracking-[0.16em] text-nook-green"
              >
                Abrir no Waze
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <iframe
              title={`Mapa da ${unit.name}`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(`${unit.street}, ${unit.neighborhood}, ${unit.city}`)}&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="h-full min-h-[320px] w-full rounded-sm border border-border grayscale"
            />
          </div>
        </div>
      </section>

      <section id="planos" className="scroll-mt-20 border-b border-border py-16 lg:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-4">
            <span className="eyebrow">Planos e investimento</span>
            <h2 className="display mt-5 text-3xl sm:text-4xl">
              Escolha o formato que acompanha sua rotina.
            </h2>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-muted-foreground">
              Todos os valores são mensais. Em cada plano, o mesmo método: treino preparado,
              acompanhado e registrado.
            </p>
          </div>
          <div className="lg:col-span-8">
            <PlansTable slug={unit.slug} />
          </div>
        </div>
      </section>

      <section className="border-b border-border py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <span className="eyebrow">Avaliações</span>
          <h2 className="display mt-5 text-3xl sm:text-4xl">Quem treina nesta unidade.</h2>
          <div className="mt-10">
            <Reviews only={unit.slug} />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-24">
        <img
          src={unit.images[unit.images.length - 1].src}
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 h-full w-full object-cover ${unit.photosPending ? "blur-md scale-105" : ""}`}
        />
        <div className="absolute inset-0 bg-black/75" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="display max-w-2xl text-4xl sm:text-5xl">
            {unit.ctaTitle ?? `Venha conhecer a ${unit.shortName}.`}
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-white/75">
            Day Use Nook por {formatBRL(DAY_USE.price)}: conheça o método e o acompanhamento na
            prática. {DAY_USE.credit}
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                track("click_experimental", { where: "unit_cta_final", unit: slug });
                openTrialDialog("unit_cta_final");
              }}
              className="inline-flex items-center rounded-sm bg-nook-green px-7 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-black"
            >
              Garanta seu Day Use →
            </button>
            <a
              href={whatsappUrl(unit)}
              target="_blank"
              rel="noreferrer"
              onClick={() => track("click_whatsapp", { where: "unit_cta_final", unit: slug })}
              className="inline-flex items-center rounded-sm border border-white/30 px-7 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-white"
            >
              Falar com a unidade
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
